/* eslint-disable @typescript-eslint/require-await */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import {
  action,
  thunk,
  Action as EzPzAction,
  Thunk as EzPzThunk,
} from 'easy-peasy';

import {
  ArchitectFilterableField,
  ArchitectFilterSectionInfo,
  ArchitectFilterSectionState,
  ArchitectFilterState,
  isArchitectFilterableField,
  OrderableField,
  LoadStatus,
  ArchitectSceneValuesGetResponse,
} from '../types';
import { BASE_ORG_API } from '../utils/constants';
import {
  // setFiltersInUrl,
  setOrderFieldInUrl,
  UrlArchitectSceneFilterState,
} from '../utils/searchParams';
import { fetchWithStatus } from '../utils/state';

/// ///////////////////////////////////////////////////////////////////////////
// Constants
/// ///////////////////////////////////////////////////////////////////////////

const FIELD_NAMES: Map<ArchitectFilterableField, string> = new Map([
  [ArchitectFilterableField.created_by, 'Creator'],
  [ArchitectFilterableField.tags, 'Tags'],
]);

const DEFAULT_ORDER = OrderableField.created_at_dsc;

/// ///////////////////////////////////////////////////////////////////////////
// Helper functions
/// ///////////////////////////////////////////////////////////////////////////

export const fieldNameToFilterSection: (
  fieldName: string | ArchitectFilterableField
) => ArchitectFilterSectionInfo = (fieldName) => {
  if (!isArchitectFilterableField(fieldName)) {
    throw new Error(
      `The field ${fieldName} was not filterable. Cannot include it in a filter section info.`
    );
  }

  const field = fieldName as ArchitectFilterableField;
  return {
    filterField: field,
    name: FIELD_NAMES.get(field) || '',
  };
};

const architectSceneValuesToFilterState = (
  architectSceneValuesResp: ArchitectSceneValuesGetResponse
) => {
  console.log(architectSceneValuesResp);
  const options = architectSceneValuesResp.map((fieldValues) => {
    const optionsForField: ArchitectFilterSectionState = {
      sectionInfo: fieldNameToFilterSection(fieldValues.field_name),
      // filter out null values
      values: fieldValues.values
        .filter((val) => val)
        // Convert numbers to strings
        .map((val) => String(val))
        // Convert to DropdownData
        .map((val) => ({ name: val })),
    };

    return optionsForField;
  });

  return options;
};

/// ///////////////////////////////////////////////////////////////////////////
// State Model
/// ///////////////////////////////////////////////////////////////////////////

// Most of the state values are optional because they will be first
// undefined, and then initialized based on the URL query parameters in
// the DriveList component
export type State = {
  orderByField?: OrderableField;
  architectFilterOptions?: ArchitectFilterSectionState[];
  activeFilters?: ArchitectFilterState;
  architectFilterOptionsLoadStatus: LoadStatus;
};

export const defaultState: State = {
  architectFilterOptionsLoadStatus: 'notStarted',
};

export interface ArchitectSceneListStateModel extends State {
  setOrderByField: EzPzAction<ArchitectSceneListStateModel, OrderableField>;
  setDefaultOrderingInUrl: EzPzThunk<ArchitectSceneListStateModel>;
  // updateDataType is 'setDataType' with side effects
  loadFilterOptions: EzPzThunk<ArchitectSceneListStateModel, number>;
  setArchitectFilterOptionsLoadStatus: EzPzAction<
    ArchitectSceneListStateModel,
    LoadStatus
  >;
  setFilterOptions: EzPzAction<
    ArchitectSceneListStateModel,
    ArchitectFilterSectionState[]
  >;
  setActiveFilters: EzPzAction<
    ArchitectSceneListStateModel,
    ArchitectFilterState
  >;
  updateFiltersBasedOnURL: EzPzThunk<
    ArchitectSceneListStateModel,
    UrlArchitectSceneFilterState
  >;
  setArchitectFilterOptions: EzPzAction<
    ArchitectSceneListStateModel,
    ArchitectFilterSectionState[]
  >;
}

const queryUrl = (orgId: number) => {
  let fieldNamesQueryParam = '';
  const fieldNames: ArchitectFilterableField[] = [
    ...Object.values(ArchitectFilterableField),
  ];
  fieldNames.forEach((fieldName) => {
    fieldNamesQueryParam = fieldNamesQueryParam.concat(
      `&field_name=${fieldName}`
    );
  });
  return `${BASE_ORG_API}/${orgId}/architect_scene_values/?${fieldNamesQueryParam}`;
};

export const model: ArchitectSceneListStateModel = {
  ...defaultState,
  setOrderByField: action((state, newOrderField) => {
    state.orderByField = newOrderField;
  }),
  setDefaultOrderingInUrl: thunk(() => {
    // Reset the ordering field to its default
    setOrderFieldInUrl(DEFAULT_ORDER);
  }),
  setFilterOptions: action((state, filterOptions) => {
    state.architectFilterOptions = filterOptions;
  }),
  updateFiltersBasedOnURL: thunk((actions, urlFilters) => {
    const filterState: ArchitectFilterState = Object.entries(
      urlFilters.filters
    ).map(([fieldName, values]) => ({
      sectionInfo: fieldNameToFilterSection(fieldName),
      values: values.map((val) => ({
        name: val,
      })),
    }));

    actions.setActiveFilters(filterState);
  }),
  setActiveFilters: action((state, newFilters) => {
    state.activeFilters = newFilters;
  }),
  loadFilterOptions: thunk(async (actions, orgId) => {
    try {
      fetchWithStatus(
        actions.setArchitectFilterOptionsLoadStatus,
        (architectSceneValues: ArchitectSceneValuesGetResponse) => {
          actions.setArchitectFilterOptions(
            architectSceneValuesToFilterState(architectSceneValues)
          );
        },
        queryUrl(orgId)
      );
    } catch (err) {
      console.warn('Failed to load drive values: ', err);
    }
  }),
  setArchitectFilterOptionsLoadStatus: action((state, status) => {
    state.architectFilterOptionsLoadStatus = status;
  }),
  setArchitectFilterOptions: action((state, filterOptions) => {
    state.architectFilterOptions = filterOptions;
  }),
};
